import { IconButton, Popover, SvgIcon } from '@material-ui/core';
import { loadedImagesErrors } from '@src/actions/common';
import { MoodIcon } from '@src/components/ChatMessageComposer/ComposerTextInput/SvgIcons';
import CustomEmojiPicker from '@src/components/CustomEmojiPicker';
import { MediaType, MessagingProvider } from '@src/model/frontendmodel';
import { queueChatMessage } from '@src/reducers/chatMessageQueue';
import { changeWrittenMessage } from '@src/reducers/chats';
import { isComposerToolbarDisabled } from '@src/selectors/agentConsole';
import {
  activeChatSelector,
  activeWrittenMessageSelector,
} from '@src/selectors/chats';
import { formatBytes } from '@src/utils/fileUtils';
import { getDimensionsImage } from '@src/utils/imageUtils';
import mediaUtils, {
  isStickerImageTypeAllowed,
  MAX_ALLOWED_HEIGHT_STICKER,
  MAX_ALLOWED_SIZE_STICKER,
  MAX_ALLOWED_WIDTH_STICKER,
} from '@src/utils/mediaUtils';
import 'emoji-mart/css/emoji-mart.css';
import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

interface IEmojiPickerIcon {
  textInputRef: React.RefObject<HTMLDivElement | null>;
}

const EmojiPickerIcon: React.FC<IEmojiPickerIcon> = ({ textInputRef }) => {
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [focus, setFocus] = useState(false);

  const activeChat = useSelector(activeChatSelector);
  const activeWrittenMessage = useSelector(activeWrittenMessageSelector);
  const composerToolbarDisabled = useSelector(isComposerToolbarDisabled);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const attachSticker = async (file: File) => {
    if (!activeChat?.id) return;
    if (isStickerImageTypeAllowed(file.type)) {
      const errorMsg = { file, notAllowedFileMsg: '' };
      const dimensionsImage = await getDimensionsImage(file);

      if (
        dimensionsImage.width !== MAX_ALLOWED_WIDTH_STICKER ||
        dimensionsImage.height !== MAX_ALLOWED_HEIGHT_STICKER
      ) {
        errorMsg.notAllowedFileMsg = formatMessage(
          { id: 'error.allowedDimensionsSticker' },
          {
            dimensions: `${MAX_ALLOWED_WIDTH_STICKER}px X ${MAX_ALLOWED_HEIGHT_STICKER}px`,
          }
        );
      }

      if (file.size > MAX_ALLOWED_SIZE_STICKER) {
        errorMsg.notAllowedFileMsg = formatMessage(
          { id: 'error.maxSizeSticker' },
          { maxSize: formatBytes(MAX_ALLOWED_SIZE_STICKER) }
        );
      }

      if (!errorMsg.notAllowedFileMsg?.length) {
        dispatch(
          queueChatMessage(
            { type: MediaType.STICKER, file },
            activeChat.id,
            activeChat?.provider
          )
        );
      } else {
        dispatch(loadedImagesErrors(errorMsg));
      }
    }
  };

  const handleAppendEmoticon = (emoticon: string) => {
    dispatch(
      changeWrittenMessage({
        chatId: activeChat?.id,
        message: activeWrittenMessage + emoticon,
      })
    );

    handleClose();

    setFocus(true);
  };

  useEffect(() => {
    if (textInputRef.current && focus) {
      setTimeout(() => {
        textInputRef.current?.focus();
        setFocus(false);
      }, 500);
    }
  }, [focus, textInputRef]);

  return (
    <>
      <IconButton
        onClick={handleClick}
        style={{ padding: 0 }}
        disabled={composerToolbarDisabled}
      >
        <SvgIcon fontSize="small">{MoodIcon}</SvgIcon>
      </IconButton>
      <Popover
        open={Boolean(anchorEl)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        marginThreshold={55}
        anchorEl={anchorEl}
        onClose={handleClose}
      >
        <CustomEmojiPicker
          handleSendSticker={attachSticker}
          handleAppendEmoticon={handleAppendEmoticon}
          withUploadOption
          withStickersOption={mediaUtils.isMediaTypeSupported(
            MediaType.STICKER,
            activeChat?.provider as MessagingProvider
          )}
        />
      </Popover>
    </>
  );
};

export default EmojiPickerIcon;
